import { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestEmailChange, confirmEmailChange } from '../../api/auth';
import { ApiRequestError } from '../../api/errors';
import type { User } from '../../types';

interface Props {
  /** The user's current login email. */
  email: string;
  role: User['role'];
  /** Called with the new address once the API confirms the change. */
  onChanged: (newEmail: string) => void;
}

type Step = 'idle' | 'enter-email' | 'enter-code';

const REQUEST_ERRORS: Record<string, string> = {
  invalid_email: 'Enter a valid email address.',
  same_email: 'That is already your login email.',
  email_in_use: 'That email address is already in use.',
  too_many_requests: 'Too many codes requested. Try again in an hour.',
};
const CONFIRM_ERRORS: Record<string, string> = {
  no_pending_request: 'Your code expired. Request a new code.',
  too_many_attempts: 'Too many incorrect codes. Request a new code.',
  email_in_use: 'That email address is already in use.',
  hanko_error: "We couldn't update your login. Nothing was changed — try again.",
  mirror_failed:
    'Your login email was updated, but our records could not be synced. Contact support@cloudunifi.com.',
};
const GENERIC = 'Something went wrong. Try again or contact support@cloudunifi.com.';

function errorFor(e: unknown, table: Record<string, string>): string {
  if (e instanceof ApiRequestError) {
    if (e.slug === 'invalid_code') {
      const n = e.attemptsRemaining ?? 0;
      return `That code is not correct. ${n} ${n === 1 ? 'attempt' : 'attempts'} remaining.`;
    }
    return (e.slug && table[e.slug]) ?? GENERIC;
  }
  return GENERIC;
}

const PRIMARY_BTN =
  'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors';
const SECONDARY_BTN =
  'px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors';
const INPUT =
  'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

/**
 * Self-service login email change. portal-api emails a 6-digit code to the
 * new address; on confirm it rewrites the login in Hanko and mirrors D1.
 */
export default function LoginEmailSection({ email, role, onChanged }: Props) {
  const [step, setStep] = useState<Step>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const showBillingHint = role === 'admin' || role === 'billing';

  const reset = () => {
    setStep('idle');
    setNewEmail('');
    setCode('');
    setError(null);
    setPending(false);
  };

  const sendCode = async () => {
    setPending(true);
    setError(null);
    try {
      await requestEmailChange(newEmail.trim());
      setCode('');
      setStep('enter-code');
    } catch (e) {
      setError(errorFor(e, REQUEST_ERRORS));
    } finally {
      setPending(false);
    }
  };

  const verify = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await confirmEmailChange(code.trim());
      setSuccess(`Login email updated to ${result.email}.`);
      onChanged(result.email);
      reset();
    } catch (e) {
      const message = errorFor(e, CONFIRM_ERRORS);
      if (e instanceof ApiRequestError && e.slug === 'mirror_failed') {
        // Hanko already switched the login and the code is consumed: the
        // change is real, so leave the code step — retrying would dead-end.
        onChanged(newEmail.trim());
        reset();
      }
      setError(message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium text-gray-500 mb-1">Login email</p>
      <p className="text-sm text-gray-600 mb-2">
        This is the address you sign in with.
        {showBillingHint && (
          <>
            {' '}
            To change your billing email, use the{' '}
            <Link to="/billing" className="text-blue-600 hover:text-blue-800 hover:underline">
              Billing portal
            </Link>
            .
          </>
        )}
      </p>

      {step === 'idle' && (
        <div className="flex items-center justify-between">
          <p className="text-gray-900">{email}</p>
          <button
            type="button"
            onClick={() => {
              setSuccess(null);
              setStep('enter-email');
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Change
          </button>
        </div>
      )}

      {step === 'enter-email' && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode();
          }}
        >
          <label htmlFor="new-email-input" className="block text-sm text-gray-700">
            New email address
          </label>
          <input
            id="new-email-input"
            type="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={`w-full ${INPUT}`}
            autoFocus
            required
          />
          <p className="text-xs text-gray-500">
            We&apos;ll email a 6-digit code to this address to confirm you own it.
          </p>
          <div className="flex gap-2">
            <button type="submit" disabled={pending || !newEmail.trim()} className={PRIMARY_BTN}>
              {pending ? 'Sending...' : 'Send code'}
            </button>
            <button type="button" onClick={reset} disabled={pending} className={SECONDARY_BTN}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {step === 'enter-code' && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p className="text-sm text-gray-700">
            We sent a code to <span className="font-medium">{newEmail.trim()}</span>. It expires in
            15 minutes.
          </p>
          <label htmlFor="email-code-input" className="block text-sm text-gray-700">
            Verification code
          </label>
          <input
            id="email-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className={`w-40 tracking-widest ${INPUT}`}
            autoFocus
            required
          />
          <div className="flex gap-2">
            <button type="submit" disabled={pending || code.length !== 6} className={PRIMARY_BTN}>
              {pending ? 'Verifying...' : 'Verify'}
            </button>
            <button type="button" disabled={pending} onClick={() => void sendCode()} className={SECONDARY_BTN}>
              Resend code
            </button>
            <button type="button" onClick={reset} disabled={pending} className={SECONDARY_BTN}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-600 mt-2">
          {error}
        </p>
      )}
      {success && <p className="text-sm text-green-700 mt-2">{success}</p>}
    </div>
  );
}
